import { defaultLanguage, LanguageDetails } from '@/types/localisation';
import { ActiveMessage, Answers, ErrorModel, MessageModel } from '@/types/message';
import { addMissingMessage } from '@/utils/contextUtils/addMissingMessage';
import { Dispatch, createContext, useContext, useReducer } from 'react';

export interface ChatContext {
  messages: MessageModel[];
  activeMessage: ActiveMessage | null;
  activeError: ErrorModel | null;
  language: LanguageDetails;
  isSurveyTesting: boolean;
}

export enum ChatActionKind {
  ADD_MESSAGE = 'ADD_MESSAGE',
  UPDATE_ACTIVE_MESSAGE = 'UPDATE_ACTIVE_MESSAGE',
  RESET_CHAT = 'RESET_CHAT',
  UPDATE_ACTIVE_ERROR = 'UPDATE_ACTIVE_ERROR',
  UPDATE_LANGUAGE = 'UPDATE_LANGUAGE',
  UPDATE_IS_SURVEY_TESTING = 'UPDATE_IS_SURVEY_TESTING',
  RESET_MESSAGES = 'RESET_MESSAGES'

  // Add more when needed
}

export type ChatAction =
  | AddMessageAction
  | UpdateActiveMessageAction
  | ResetChatAction
  | UpdateActiveErrorAction
  | UpdateLanguageAction
  | UpdateIsSurveyTesting
  | ResetMessages;

export interface AddMessageAction {
  type: ChatActionKind.ADD_MESSAGE;
  payload: MessageModel;
}

export interface UpdateActiveMessageAction {
  type: ChatActionKind.UPDATE_ACTIVE_MESSAGE;
  payload: Answers;
}

export interface ResetChatAction {
  type: ChatActionKind.RESET_CHAT;
}

export interface UpdateActiveErrorAction {
  type: ChatActionKind.UPDATE_ACTIVE_ERROR;
  payload: ErrorModel;
}

export interface UpdateLanguageAction {
  type: ChatActionKind.UPDATE_LANGUAGE;
  payload: LanguageDetails;
}

export interface UpdateIsSurveyTesting {
  type: ChatActionKind.UPDATE_IS_SURVEY_TESTING;
  payload: boolean;
}

export interface ResetMessages {
  type: ChatActionKind.RESET_MESSAGES;
}

export const defaultChatState: ChatContext = {
  messages: [],
  activeMessage: null,
  activeError: null,
  language: defaultLanguage,
  isSurveyTesting: false
};

export function chatReducer(state: ChatContext, action: ChatAction): ChatContext {
  switch (action.type) {
    case ChatActionKind.ADD_MESSAGE:
      return {
        ...state,
        messages: addMissingMessage(state.messages, action.payload),
        activeMessage: { message: action.payload, shownAt: Date.now() }
      };

    case ChatActionKind.UPDATE_ACTIVE_MESSAGE:
      return {
        ...state,
        activeMessage: state.activeMessage ? { ...state.activeMessage, answers: action.payload } : state.activeMessage,
        activeError: null
      };

    case ChatActionKind.RESET_CHAT:
      return defaultChatState;

    case ChatActionKind.UPDATE_ACTIVE_ERROR:
      return {
        ...state,
        activeError: action.payload
      };

    case ChatActionKind.UPDATE_LANGUAGE:
      return {
        ...state,
        language: action.payload
      };

    case ChatActionKind.UPDATE_IS_SURVEY_TESTING:
      return {
        ...state,
        isSurveyTesting: action.payload
      };

    case ChatActionKind.RESET_MESSAGES:
      return {
        ...state,
        messages: defaultChatState.messages,
        activeMessage: defaultChatState.activeMessage
      };

    default:
      throw new Error(`Unhandled action type: ${(action as ChatAction).type}`);
  }
}
export const chatDispatchContext = createContext<Dispatch<ChatAction>>(null as never);
export const chatContext = createContext<ChatContext>(null as never);

type ChatProviderProps = { children: React.ReactNode; optionalContext?: ChatContext | undefined };

export function ChatProvider({ children, optionalContext }: ChatProviderProps) {
  const initialState = optionalContext ? optionalContext : defaultChatState;

  const [{ messages, activeMessage, activeError, language, isSurveyTesting }, dispatch] = useReducer(chatReducer, initialState);

  return (
    <chatDispatchContext.Provider value={dispatch}>
      <chatContext.Provider value={{ messages, activeMessage, activeError, language, isSurveyTesting }}>{children}</chatContext.Provider>
    </chatDispatchContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(chatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const useChatDispatch = () => {
  const context = useContext(chatDispatchContext);
  if (context === undefined) {
    throw new Error('useChatDispatch must be used within a ChatProvider');
  }
  return context;
};
